import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { AgremoAnalysis } from '../data-models/agremo-analysis.model';
import { AgremoAnalysisFilters } from '../request-models/agremo-analysis-filters.model';
import { AgremoAnalysisDetail } from '../response-models/agremo-analysis-detail.model';
import { AgremoProcessPendingResult } from '../response-models/agremo-process-pending-result.model';
import { Page } from '../response-models/page.model';
import { isRecord } from '../utils/is-record';
import { countOrZero } from '../utils/wire-value-utils';

/**
 * Agremo analyses across all organizations: `/admin/agremo/analyses` (hub `admin-agremo-analyses-controller.ts`),
 * paged with `limit`. Also retry-download and process-pending, which live under the same endpoint.
 */
@Injectable({ providedIn: 'root' })
export class AgremoAnalysisService extends BaseService<AgremoAnalysis> {
  constructor() {
    super('admin/agremo/analyses', AgremoAnalysis);
  }

  /** GET /admin/agremo/analyses: one page, filtered by organization, status and requested-date range. */
  listAnalyses(filters: AgremoAnalysisFilters = {}): Observable<Page<AgremoAnalysis>> {
    return this.getPage({ query: toAnalysisQuery(filters) });
  }

  /** GET /admin/agremo/analyses/:id with map details and result files. */
  getAnalysis(analysisId: string): Observable<AgremoAnalysisDetail | null> {
    return this.request('GET', { id: analysisId }, toDetailOrNull);
  }

  /** POST /admin/agremo/analyses/:id/retry-download: re-queues the result file download. */
  retryDownload(analysisId: string): Observable<void> {
    return this.request('POST', { id: analysisId, path: 'retry-download' }, () => undefined, {});
  }

  /** POST /admin/agremo/analyses/process-pending: queues pending analyses that credits can now cover. */
  processPending(): Observable<AgremoProcessPendingResult> {
    return this.request('POST', { path: 'process-pending' }, toProcessPendingResult, {});
  }
}

/** Query for the analyses lists. Blank filters and the `'all'` status are not sent. */
export function toAnalysisQuery(filters: AgremoAnalysisFilters): Record<string, string | number | undefined> {
  return {
    page: filters.page,
    limit: filters.limit,
    organizationId: filters.organizationId?.trim() || undefined,
    status: filters.status && filters.status !== 'all' ? filters.status : undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
  };
}

function toDetailOrNull(data: unknown): AgremoAnalysisDetail | null {
  return data === null || data === undefined ? null : new AgremoAnalysisDetail(data);
}

function toProcessPendingResult(data: unknown): AgremoProcessPendingResult {
  const source = isRecord(data) ? data : {};
  return {
    processed: countOrZero(source['processed']),
    totalPending: countOrZero(source['totalPending']),
    remainingPending: countOrZero(source['remainingPending']),
  };
}
